/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import {
  FileText, Search, Megaphone, Link2, ShieldCheck, Cpu, TrendingUp, Users
} from 'lucide-react';

/* ─── colour tokens (match existing navy palette) ─── */
const C = {
  bg:        '#0a1628',
  card:      '#0f1e38',
  cardBorder:'rgba(255,255,255,0.07)',
  gold:      '#f0c060',
  cyan:      '#38bdf8',
  purple:    '#a78bfa',
  pink:      '#f472b6',
  green:     '#34d399',
  orange:    '#fb923c',
  text:      '#e2e8f0',
  muted:     'rgba(255,255,255,0.45)',
};

/* ─── data ─── */
const channels = [
  { label: 'Website',   pct: 38, color: C.cyan   },
  { label: 'Instagram', pct: 30, color: C.purple  },
  { label: 'WhatsApp',  pct: 20, color: C.green   },
  { label: 'TikTok',    pct: 8,  color: C.pink    },
  { label: 'Facebook',  pct: 4,  color: C.orange  },
];

const trendPoints = [230, 310, 280, 460, 390, 540, 610];
const trendLabels = ['1 Mei','6 Mei','11 Mei','15 Mei','19 Mei','22 Mei','29 Mei'];

const pipeline = [
  { label: 'New Lead',   value: 1248, color: C.cyan   },
  { label: 'Qualified',  value: 682,  color: C.purple },
  { label: 'Follow Up',  value: 342,  color: C.pink   },
  { label: 'Konsultasi', value: 236,  color: C.orange },
  { label: 'Closed',     value: 128,  color: C.green  },
];

const categories = [
  { label: 'Perdata',  pct: 35, color: C.cyan   },
  { label: 'Pidana',   pct: 28, color: C.purple },
  { label: 'Keluarga', pct: 22, color: C.pink   },
  { label: 'Bisnis',   pct: 12, color: C.orange },
  { label: 'Lainnya',  pct: 8,  color: C.green  },
];


/* ─── helpers ─── */
function card(extra = {}) {
  return {
    background: C.card,
    border: `1px solid ${C.cardBorder}`,
    borderRadius: 14,
    padding: '18px 20px',
    ...extra,
  };
}

/* ──────────────── DONUT CHART ──────────────── */
function DonutChart() {
  const r = 54, cx = 70, cy = 70;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const slices = channels.map(ch => {
    const dash = (ch.pct / 100) * circ;
    const slice = { ...ch, dash, offset };
    offset += dash;
    return slice;
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width={140} height={140} style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={18} />
        {slices.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={18}
            strokeDasharray={`${s.dash} ${circ - s.dash}`}
            strokeDashoffset={circ / 4 - s.offset}
            style={{ transition: 'stroke-dasharray 0.6s' }}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill={C.gold} fontSize={18} fontWeight={700}>1,248</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill={C.muted} fontSize={9}>Total Leads</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {channels.map(ch => (
          <div key={ch.label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: ch.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: C.muted, flex: 1 }}>{ch.label}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{ch.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────── LINE / SPARKLINE CHART ──────────────── */
function TrendChart() {
  const W = 280, H = 110;
  const min = Math.min(...trendPoints), max = Math.max(...trendPoints);
  const pad = { t: 12, b: 26, l: 28, r: 8 };
  const iw = W - pad.l - pad.r, ih = H - pad.t - pad.b;

  const pts = trendPoints.map((v, i) => ({
    x: pad.l + (i / (trendPoints.length - 1)) * iw,
    y: pad.t + (1 - (v - min) / (max - min)) * ih,
  }));

  const poly = pts.map(p => `${p.x},${p.y}`).join(' ');

  // area fill
  const areaPath = [
    `M ${pts[0].x},${pts[0].y}`,
    ...pts.slice(1).map(p => `L ${p.x},${p.y}`),
    `L ${pts[pts.length - 1].x},${pad.t + ih}`,
    `L ${pts[0].x},${pad.t + ih}`,
    'Z',
  ].join(' ');

  // y-axis labels
  const yTicks = [230, 370, 460, 610];

  return (
    <svg width={W} height={H}>
      <defs>
        <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.cyan} stopOpacity="0.35" />
          <stop offset="100%" stopColor={C.cyan} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* grid lines */}
      {yTicks.map((t, i) => {
        const y = pad.t + (1 - (t - min) / (max - min)) * ih;
        return (
          <g key={i}>
            <line x1={pad.l} x2={pad.l + iw} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
            <text x={pad.l - 4} y={y + 4} fontSize={8} fill={C.muted} textAnchor="end">{t}</text>
          </g>
        );
      })}
      {/* area */}
      <path d={areaPath} fill="url(#trendGrad)" />
      {/* line */}
      <polyline points={poly} fill="none" stroke={C.cyan} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {/* dots */}
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill={C.cyan} stroke={C.card} strokeWidth={1.5} />
      ))}
      {/* x labels */}
      {pts.map((p, i) => (
        i % 2 === 0 && (
          <text key={i} x={p.x} y={H - 4} fontSize={8} fill={C.muted} textAnchor="middle">{trendLabels[i]}</text>
        )
      ))}
    </svg>
  );
}

/* ──────────────── PIPELINE FUNNEL ──────────────── */
function PipelineFunnel() {
  const max = pipeline[0].value;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {pipeline.map((p, i) => {
        const widthPct = 40 + (p.value / max) * 60;
        return (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* funnel bar centred */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: `${widthPct}%`,
                height: 24,
                background: `linear-gradient(90deg, ${p.color}55, ${p.color})`,
                borderRadius: 4,
                border: `1px solid ${p.color}60`,
                display: 'flex', alignItems: 'center', paddingLeft: 8,
              }}>
                <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>{p.label}</span>
              </div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: p.color, minWidth: 36, textAlign: 'right' }}>
              {p.value.toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────── CATEGORY BARS ──────────────── */
function CategoryBars() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {categories.map(cat => (
        <div key={cat.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: C.muted }}>{cat.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: cat.color }}>{cat.pct}%</span>
          </div>
          <div style={{ height: 7, background: 'rgba(255,255,255,0.07)', borderRadius: 99 }}>
            <div style={{
              height: '100%', width: `${cat.pct}%`,
              background: `linear-gradient(90deg, ${cat.color}88, ${cat.color})`,
              borderRadius: 99,
              transition: 'width 0.8s cubic-bezier(.4,0,.2,1)',
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ──────────────── ANIMATED COUNTER ──────────────── */
function Counter({ target, suffix = '', decimals = 0 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null;
    const duration = 1200;
    function step(ts) {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(+(eased * target).toFixed(decimals));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }, [target, decimals]);
  return <>{decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString()}{suffix}</>;
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function DashboardOverview() {
  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: C.bg,
      minHeight: '100%',
      padding: '4px 0 24px',
    }}>

      {/* ── Page Title ── */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <h1 style={{
          fontSize: 18, fontWeight: 700, letterSpacing: 2,
          color: C.text, margin: 0, textTransform: 'uppercase',
        }}>
          Lead Dashboard
        </h1>
        <div style={{ width: 60, height: 2, background: C.gold, margin: '8px auto 0', borderRadius: 2 }} />
      </div>

      {/* ══ ROW 1: 5 panels ══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.1fr 1.1fr 1.3fr 1fr 1fr',
        gap: 14,
        marginBottom: 14,
      }}>

        {/* 1. LEAD OVERVIEW */}
        <div style={card()}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.gold, marginBottom: 14, textTransform: 'uppercase' }}>
            Lead Overview
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Total Leads',           val: 1248, color: C.cyan,   suffix: '' },
              { label: 'Qualified Leads',        val: 682,  color: C.purple, suffix: '' },
              { label: 'Konsultasi Dijadwalkan', val: 236,  color: C.pink,   suffix: '' },
            ].map(item => (
              <div key={item.label} style={{
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 10, padding: '10px 12px',
                border: `1px solid rgba(255,255,255,0.06)`,
              }}>
                <div style={{ fontSize: 9, color: C.muted, marginBottom: 4, lineHeight: 1.4 }}>{item.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: item.color, lineHeight: 1 }}>
                  <Counter target={item.val} suffix={item.suffix} decimals={item.decimals || 0} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. LEADS BY CHANNEL */}
        <div style={card()}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.gold, marginBottom: 12, textTransform: 'uppercase' }}>
            Leads by Channel
          </div>
          <DonutChart />
        </div>

        {/* 3. LEADS TREND */}
        <div style={card()}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.gold, marginBottom: 8, textTransform: 'uppercase' }}>
            Leads Trend
          </div>
          <TrendChart />
        </div>

        {/* 5. TOP CASE CATEGORY */}
        <div style={card()}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.gold, marginBottom: 14, textTransform: 'uppercase' }}>
            Top Case Category
          </div>
          <CategoryBars />
        </div>
      </div>
    </div>
  );
}
